import { useRef, useEffect } from "react";

const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

interface ReactionPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onReact: (reaction: string) => void;
  position?: { top: number; left: number };
}

export default function ReactionPicker({ isOpen, onClose, onReact, position }: ReactionPickerProps) {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className="fixed z-[100] bg-background-50 rounded-full border border-background-200/70 shadow-lg px-1.5 py-1.5 flex items-center gap-0.5 animate-in zoom-in-95 fade-in duration-150"
      style={
        position
          ? { top: `${position.top - 48}px`, left: `${position.left}px` }
          : undefined
      }
    >
      {QUICK_REACTIONS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => {
            onReact(emoji);
            onClose();
          }}
          className="w-9 h-9 flex items-center justify-center text-lg hover:scale-125 cursor-pointer transition-transform rounded-full hover:bg-background-100"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}